// pages/payments/PaymentFormModal.jsx
// Record a payment PAID OUT to a karigar.
import { useEffect, useMemo, useState } from "react";
import { karigarsAPI, paymentsAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { Modal, Spinner } from "../../components/ui";
import { PAYMENT_MODES, todayISO } from "../../utils/helpers";

export default function PaymentFormModal({ onClose, onSaved }) {
  const toast = useToast();

  const [karigars, setKarigars]       = useState([]);
  const [loadingKarigars, setLoading] = useState(true);
  const [karigarSearch, setKarigarSearch] = useState("");
  const [saving, setSaving]           = useState(false);

  const [form, setForm] = useState({
    karigarId: "",
    amount:    "",
    kasar:     "",
    mode:      PAYMENT_MODES[0],
    date:      todayISO(),
    remarks:   "",
  });

  useEffect(() => {
    setLoading(true);
    karigarsAPI
      .list()
      .then((d) => setKarigars(Array.isArray(d) ? d : []))
      .catch(() => toast("Failed to load karigars", "error"))
      .finally(() => setLoading(false));
  }, []);

  const filteredKarigars = useMemo(() => {
    const q = karigarSearch.trim().toLowerCase();
    if (!q) return karigars;
    return karigars.filter(
      (k) =>
        (k.name ?? "").toLowerCase().includes(q) ||
        (k.workType ?? "").toLowerCase().includes(q)
    );
  }, [karigars, karigarSearch]);

  const selectedKarigar = karigars.find((k) => String(k.id) === String(form.karigarId));

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Karigar_Payment.php applies exactly this to ledger_master:
  //   LM_Balance = LM_Balance - PD_Amount + PD_Kasar
  // Projecting it here means the effect on the balance is visible BEFORE saving, rather
  // than discovered afterwards on the karigar list.
  const amtNum   = Number(form.amount) || 0;
  const kasarNum = Number(form.kasar)  || 0;
  const balanceAfter = selectedKarigar
    ? (selectedKarigar.balance || 0) - amtNum + kasarNum
    : null;

  const handleSave = async () => {
    if (!form.karigarId)   return toast("Please select a karigar", "error");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast("Enter a valid amount", "error");
    if (kasarNum < 0)     return toast("Kasar can't be negative", "error");
    if (!form.date)       return toast("Please pick a date", "error");

    setSaving(true);
    try {
      await paymentsAPI.create({
        karigarId: form.karigarId,
        amount:    amt,
        kasar:     kasarNum,
        mode:      form.mode,
        date:      form.date,
        remarks:   form.remarks.trim(),
      });
      toast("Payment recorded 💰");
      onSaved?.();
    } catch (e) {
      console.error("[PaymentFormModal] save error:", e);
      // The endpoint rolls the insert back and explains why ("Ledger not found…"), so
      // its message is far more useful than a generic failure line.
      toast(e?.message || "Failed to save payment", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Record Payment"
      subtitle="Money paid out to a karigar"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loadingKarigars}>
            {saving ? <Spinner /> : "Save Payment"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        {/* Karigar picker */}
        <div className="form-group form-full">
          <label className="form-label">Karigar</label>
          {karigars.length > 8 && (
            <div className="search-bar" style={{ marginBottom: 8 }}>
              <span className="search-icon">🔍</span>
              <input
                className="form-input"
                placeholder="Filter karigars by name or work type…"
                value={karigarSearch}
                onChange={(e) => setKarigarSearch(e.target.value)}
              />
            </div>
          )}
          <select
            className="form-select"
            value={form.karigarId}
            onChange={(e) => set("karigarId", e.target.value)}
            disabled={loadingKarigars}
          >
            <option value="">{loadingKarigars ? "Loading karigars…" : "Select a karigar"}</option>
            {filteredKarigars.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}{k.workType ? ` — ${k.workType}` : ""}
              </option>
            ))}
          </select>
          {selectedKarigar && (
            <div className="td-muted" style={{ marginTop: 6, fontSize: 12 }}>
              Current balance: <strong>₹{(selectedKarigar.balance || 0).toLocaleString()}</strong>
              {selectedKarigar.phone ? ` · ${selectedKarigar.phone}` : ""}
              {(amtNum > 0 || kasarNum > 0) && (
                <> · after this payment: <strong>₹{balanceAfter.toLocaleString()}</strong></>
              )}
            </div>
          )}
        </div>

        {/* Amount */}
        <div className="form-group">
          <label className="form-label">Amount (₹)</label>
          <input
            type="number"
            min="0"
            className="form-input"
            placeholder="0"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
          />
        </div>

        {/* Kasar — written off on settlement. Stored in PD_Kasar and added back to the
            karigar's balance by the endpoint, so it is not simply cosmetic. */}
        <div className="form-group">
          <label className="form-label">Kasar (₹) <span className="form-label">(optional)</span></label>
          <input
            type="number"
            min="0"
            className="form-input"
            placeholder="0"
            value={form.kasar}
            onChange={(e) => set("kasar", e.target.value)}
          />
        </div>

        {/* Mode */}
        <div className="form-group">
          <label className="form-label">Payment Mode</label>
          <select className="form-select" value={form.mode} onChange={(e) => set("mode", e.target.value)}>
            {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Date */}
        <div className="form-group">
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={form.date} onChange={(e) => set("date", e.target.value)} />
        </div>

        {/* Remarks */}
        <div className="form-group form-full">
          <label className="form-label">Remarks <span className="form-label">(optional)</span></label>
          <textarea
            className="form-textarea"
            rows={2}
            placeholder="Any note about this payment…"
            value={form.remarks}
            onChange={(e) => set("remarks", e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
